import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container } from '@/components/container';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useGetTransactionDetailQuery,
  useRetryTransactionNetvisorInvoiceMutation,
} from '@/services/TransactionsService';
import {
  isSubscriptionTx,
  isClubResourceTx,
  isPortWatchBuyoutTx,
  isBookingTx,
  isServiceBookingTx,
  TransactionResponse,
} from '@/types/transactions.types';
import { ResourceType } from '@/types/club-resource.types';
import { BookingStatus } from '@/types/booking-management.types';
import { formatFinnishCurrency, formatFinnishPercentage } from '@/utils';
import { extractTime } from '@/utils/helperFunctions';
import { formatDisplayDate, formatDisplayDateTime } from '@/utils/Date';
import { BillingCycle } from '@/types/subscription-management.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { selectUserPermissions } from '@/store/authSlice';

const NETVISOR_POSTING_STATUS_LABELS: Record<string, string> = {
  POSTED: 'Posted',
  FAILED: 'Failed',
  PENDING: 'Pending',
  NOT_REQUIRED: 'Not Required',
};

const getNetvisorPostingStatusBadgeClass = (status: string) => {
  switch (status) {
    case 'POSTED':
      return 'badge-success';
    case 'FAILED':
      return 'badge-danger';
    case 'PENDING':
      return 'badge-secondary';
    case 'NOT_REQUIRED':
      return 'badge-secondary';
    default:
      return 'badge-secondary';
  }
};

const getResourceTypeLabel = (typeValue: number | string): string => {
  const typeNum =
    typeof typeValue === 'string' ? parseInt(typeValue, 10) : typeValue;
  switch (typeNum) {
    case ResourceType.SAUNA_BOOKING:
      return 'Sauna Booking';
    case ResourceType.KEYS:
      return 'Keys';
    case ResourceType.ANNUAL_INSPECTIONS:
      return 'Annual Inspections';
    case ResourceType.PORT_WATCH_SHIFTS:
      return 'Port Watch Shifts';
    default:
      return 'Unknown';
  }
};

const formatBillingCycle = (val: unknown): string => {
  const num = typeof val === 'number' ? val : Number(val);
  if (num === BillingCycle.MONTHLY) return 'Monthly';
  if (num === BillingCycle.YEARLY) return 'Annual';
  if (num === BillingCycle.FIVE_DAYS) return '5 Days';
  return String(val ?? '—');
};

const formatTaxRegime = (value?: string | null): string => {
  if (!value) return '—';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getBookingStatusLabel = (statusValue: number | string): string => {
  const statusNum =
    typeof statusValue === 'string' ? parseInt(statusValue, 10) : statusValue;
  switch (statusNum) {
    case BookingStatus.PENDING:
      return 'Pending';
    case BookingStatus.CONFIRMED:
      return 'Confirmed';
    case BookingStatus.CANCELLED:
      return 'Cancelled';
    case BookingStatus.COMPLETED:
      return 'Completed';
    case BookingStatus.PENDING_CONFIRMATION:
      return 'Pending Confirmation';
    default:
      return 'Unknown';
  }
};

const getPortWatchShiftStatusLabel = (
  statusValue?: number | string | null
): string => {
  if (statusValue === null || statusValue === undefined || statusValue === '') {
    return '—';
  }

  const numericStatus =
    typeof statusValue === 'string' ? Number(statusValue) : statusValue;
  if (Number.isFinite(numericStatus)) {
    switch (numericStatus) {
      case 1:
        return 'Open';
      case 2:
        return 'Filled';
      case 3:
        return 'Completed';
      default:
        break;
    }
  }

  return String(statusValue)
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const formatDate = (isoString: string): string =>
  formatDisplayDate(new Date(isoString));

const formatTime = (isoString: string): string =>
  new Date(isoString).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

const parsePspFee = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatProductType = (value?: string | null): string => {
  if (!value) return '—';
  return value
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const formatCountryName = (code?: string | null): string => {
  if (!code) return '—';
  const upper = code.toUpperCase();
  try {
    const hasDisplayNames =
      typeof (Intl as unknown as { DisplayNames?: unknown }).DisplayNames !==
      'undefined';
    const locale =
      typeof navigator !== 'undefined' && navigator.language
        ? navigator.language
        : 'en';
    const displayNames = hasDisplayNames
      ? new (
          Intl as unknown as {
            DisplayNames: new (
              locales: string | string[],
              options: { type: 'region' }
            ) => { of: (code: string) => string | undefined | null };
          }
        ).DisplayNames([locale], { type: 'region' })
      : null;
    const name = displayNames ? displayNames.of(upper) : null;
    return name || upper;
  } catch {
    return upper;
  }
};

interface ItemsSnapshotTotals {
  net_amount?: number;
  vat_amount?: number;
  gross_amount?: number;
}

interface ItemsSnapshotItemTax {
  tax_regime?: string;
  customer_vat_id?: string | null;
  customer_country?: string | null;
  product_vat_config_id?: string;
  customer_vat_id_validation?: string;
}

interface ItemsSnapshotItem {
  tax?: ItemsSnapshotItemTax;
  vat_rate?: number;
  net_amount?: number;
  vat_amount?: number;
  gross_amount?: number;
  description?: string;
  product_type?: string;
}

interface ItemsSnapshotDiscount {
  code?: string;
  label?: string;
  discount_type?: string;
  discount_value?: number;
  discount_amount?: number;
  original_amount?: number;
  final_amount?: number;
  original_net_amount?: number;
  final_net_amount?: number;
  original_gross_amount?: number;
  final_gross_amount?: number;
}

interface ItemsSnapshot {
  items?: ItemsSnapshotItem[];
  addons?: unknown[];
  totals?: ItemsSnapshotTotals;
  discount?: ItemsSnapshotDiscount | null;
  buyout?: {
    kind?: string;
    buyout_id?: string;
    club_id?: string;
    resource_id?: string;
    duty_units?: number;
  };
}

const TransactionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const userPermissions = useSelector(selectUserPermissions);

  const {
    data: transaction,
    isLoading,
    error,
  } = useGetTransactionDetailQuery(id || '', {
    skip: !id,
  });

  const [retryNetvisorInvoice, { isLoading: isRetryingNetvisorInvoice }] =
    useRetryTransactionNetvisorInvoiceMutation();
  const [isNetvisorRetryConfirmOpen, setIsNetvisorRetryConfirmOpen] =
    useState(false);

  const hasNetvisorRetryPermission = useMemo(() => {
    return userPermissions?.some((p) =>
      p.permissions?.unique_key?.includes('netvisor_accounting_exports_retry')
    );
  }, [userPermissions]);

  const handleRetryNetvisorInvoice = async () => {
    if (!id) return;
    try {
      await retryNetvisorInvoice(id).unwrap();
      enqueueSnackbar('Netvisor invoice posting retried', {
        variant: 'success',
      });
      setIsNetvisorRetryConfirmOpen(false);
    } catch (e) {
      const err = e as unknown as { data?: unknown; error?: string };
      let message = 'Failed to retry Netvisor invoice posting';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  useEffect(() => {
    if (error) {
      const err = error as unknown as {
        data?: unknown;
        error?: string;
      } | null;
      let message = 'Failed to fetch transaction details';
      if (
        err?.data &&
        typeof err.data === 'object' &&
        'message' in (err.data as Record<string, unknown>)
      ) {
        const m = (err.data as { message?: string }).message;
        if (typeof m === 'string' && m) message = m;
      } else if (typeof err?.error === 'string' && err.error) {
        message = err.error;
      }
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'SUCCEEDED':
        return 'badge-success';
      case 'FAILED':
        return 'badge-danger';
      case 'REFUNDED':
        return 'badge-warning';
      default:
        return 'badge-secondary';
    }
  };

  const renderTransactionDetails = (tx: TransactionResponse) => {
    if (isSubscriptionTx(tx)) {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Subscription Details</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Subscription Name</p>
                  <div
                    className="prose max-w-none font-medium dark:text-white contrast:text-white"
                    dangerouslySetInnerHTML={{ __html: tx.owner.data.name }}
                  />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Subtitle</p>
                  <p className="font-medium">{tx.owner.data.name_sub_title}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Price</p>
                  <p className="font-medium">
                    {formatFinnishCurrency(tx.owner.data.price)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Billing Cycle</p>
                  <p className="font-medium">{formatBillingCycle(tx.owner.data.billing_cycle)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <div
                    className="prose prose-sm max-w-none dark:text-gray-900 contrast:text-gray-900"
                    dangerouslySetInnerHTML={{
                      __html: tx.owner.data.description || '',
                    }}
                  />
                </div>
                {(() => {
                  const snap =
                    (
                      tx.data as unknown as {
                        items_snapshot?: ItemsSnapshot | null;
                      }
                    ).items_snapshot || null;
                  const first =
                    snap?.items && snap.items.length > 0
                      ? snap.items[0]
                      : undefined;
                  const taxRegime = formatTaxRegime(
                    first?.tax?.tax_regime || null
                  );
                  if (!first?.tax?.tax_regime) return null;
                  return (
                    <div>
                      <p className="text-sm text-gray-500">Tax Regime</p>
                      <p className="font-medium">{taxRegime}</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isClubResourceTx(tx)) {
      const ownerSnap = tx.owner.items_snapshot || null;
      const ownerTotals = ownerSnap?.totals || null;
      const ownerFirstItem =
        ownerSnap?.items && ownerSnap.items.length > 0
          ? ownerSnap.items[0]
          : undefined;
      const ownerVatRate =
        typeof ownerFirstItem?.vat_rate === 'number'
          ? `${ownerFirstItem.vat_rate}%`
          : '—';
      const ownerProduct = formatProductType(
        ownerFirstItem?.product_type || null
      );
      const ownerTaxRegime = formatTaxRegime(
        ownerFirstItem?.tax?.tax_regime || null
      );
      const ownerCustomerCountry = formatCountryName(
        tx.owner.customer_country ||
          ownerFirstItem?.tax?.customer_country ||
          null
      );
      const netTotal =
        typeof ownerTotals?.net_amount === 'number'
          ? ownerTotals?.net_amount
          : typeof tx.owner.net_amount === 'string' ||
              typeof tx.owner.net_amount === 'number'
            ? Number(tx.owner.net_amount)
            : undefined;
      const vatTotal =
        typeof ownerTotals?.vat_amount === 'number'
          ? ownerTotals?.vat_amount
          : typeof tx.owner.vat_amount === 'string' ||
              typeof tx.owner.vat_amount === 'number'
            ? Number(tx.owner.vat_amount)
            : undefined;
      const grossTotal =
        typeof ownerTotals?.gross_amount === 'number'
          ? ownerTotals?.gross_amount
          : typeof tx.owner.gross_amount === 'string' ||
              typeof tx.owner.gross_amount === 'number'
            ? Number(tx.owner.gross_amount)
            : undefined;
      return (
        <div className="space-y-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Club Resource Booking Summary</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total Price</p>
                  <p className="text-2xl font-semibold">
                    {formatFinnishCurrency(tx.owner.total_price)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">{tx.owner.status}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created</p>
                  <p className="font-medium">
                    {formatDisplayDateTime(new Date(tx.owner.created_at))}
                  </p>
                </div>
                {tx.owner.payment_provider && (
                  <div>
                    <p className="text-sm text-gray-500">Payment Provider</p>
                    <p className="font-medium">
                      {tx.owner.payment_provider.toUpperCase()}
                    </p>
                  </div>
                )}
              </div>

              <div className="divider my-4"></div>
              <h4 className="font-semibold mb-3">Charge Totals</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Net Amount</p>
                  <p className="text-xl font-semibold">
                    {typeof netTotal === 'number'
                      ? formatFinnishCurrency(netTotal)
                      : '—'}
                  </p>
                </div>
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">VAT Amount</p>
                  <p className="text-xl font-semibold">
                    {typeof vatTotal === 'number'
                      ? formatFinnishCurrency(vatTotal)
                      : '—'}
                  </p>
                </div>
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Gross Amount</p>
                  <p className="text-xl font-semibold">
                    {typeof grossTotal === 'number'
                      ? formatFinnishCurrency(grossTotal)
                      : '—'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">VAT Rate</p>
                  <p className="text-lg font-semibold">{ownerVatRate}</p>
                </div>
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Product Type</p>
                  <p className="text-lg font-semibold">{ownerProduct}</p>
                </div>
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Tax Regime</p>
                  <p className="text-lg font-semibold">{ownerTaxRegime}</p>
                </div>
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">Customer Country</p>
                  <p className="text-lg font-semibold">
                    {ownerCustomerCountry}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Booked Items</h2>
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Resource Name</th>
                      <th>Type</th>
                      <th className="text-end">Quantity</th>
                      <th className="text-end">Price</th>
                      <th className="text-end">Total</th>
                      <th>Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tx.owner.items.map((item) => (
                      <tr key={item.id}>
                        <td className="font-medium">{item.resource.name}</td>
                        <td>
                          {getResourceTypeLabel(item.resource.resource_type)}
                        </td>
                        <td className="text-end">{item.quantity || '—'}</td>
                        <td className="text-end">
                          {formatFinnishCurrency(item.resource.price)}
                        </td>
                        <td className="text-end font-semibold">
                          {formatFinnishCurrency(item.total_price)}
                        </td>
                        <td className="text-sm">
                          {formatDate(item.start_time)}
                          {(item.resource.price_unit === 'PER_SLOT' ||
                            item.resource.price_unit === 'PER_HOUR') &&
                            ` ${formatTime(item.start_time)}`}{' '}
                          to {formatDate(item.end_time)}
                          {(item.resource.price_unit === 'PER_SLOT' ||
                            item.resource.price_unit === 'PER_HOUR') &&
                            ` ${formatTime(item.end_time)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isPortWatchBuyoutTx(tx)) {
      const owner = tx.owner;
      const shift = owner.shift;
      const shiftStart = shift?.shift_start
        ? formatDisplayDateTime(new Date(shift.shift_start))
        : '—';
      const shiftEnd = shift?.shift_end
        ? formatDisplayDateTime(new Date(shift.shift_end))
        : '—';
      const completedAt = owner.completed_at
        ? formatDisplayDateTime(new Date(owner.completed_at))
        : '—';

      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Port Watch Buy-Out Details</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Buy-Out ID</p>
                  <p className="font-medium">{owner.id}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Buy-Out Status</p>
                    <p className="font-medium">{owner.status}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Duty Units</p>
                    <p className="font-medium">{owner.duty_units}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="text-lg font-semibold">
                      {formatFinnishCurrency(owner.amount, {
                        currency: owner.currency || tx.data.currency,
                      })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="font-medium">{completedAt}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Obligation Period</p>
                  <p className="font-medium">
                    {owner.obligation_period_start || '—'} to{' '}
                    {owner.obligation_period_end || '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Original Shift</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Shift ID</p>
                  <p className="font-medium">{shift?.id || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Shift Time</p>
                  <p className="font-medium">
                    {shiftStart} to {shiftEnd}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Current Shift Status</p>
                  <p className="font-medium">
                    {getPortWatchShiftStatusLabel(shift?.status)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">
                    Assignment After Buy-Out
                  </p>
                  <p className="font-medium">
                    {shift?.assigned_member_id ? 'Assigned' : 'Released / Open'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="card md:col-span-2">
            <div className="card-body">
              <h2 className="card-title mb-4">Club Resource</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Club</p>
                  <p className="font-medium">{owner.club?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Resource</p>
                  <p className="font-medium">{owner.resource?.name || '—'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Resource Type</p>
                  <p className="font-medium">
                    {owner.resource
                      ? getResourceTypeLabel(owner.resource.resource_type)
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isBookingTx(tx)) {
      const berth = (tx.owner.data.berth ?? null) as {
        berth_number?: string;
        mooring_style?: string;
        price?: number;
      } | null;
      const boat = (tx.owner.data.boat_details ?? null) as {
        length?: string;
        width?: string;
        draft?: string;
        unit?: string;
      } | null;
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Booking Details</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Booking ID</p>
                  <p className="font-medium">{tx.owner.data.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Harbor Name</p>
                  <p className="font-medium">
                    {tx.owner.data.harbor?.name || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Check-in</p>
                  <p className="font-medium">
                    {formatDisplayDate(tx.owner.data.start_date.split('T')[0])}{' '}
                    {tx.owner.data.harbor?.default_check_in_time || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Check-out</p>
                  <p className="font-medium">
                    {formatDisplayDate(tx.owner.data.end_date.split('T')[0])}{' '}
                    {tx.owner.data.harbor?.default_check_out_time || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Harbor Timezone</p>
                  <p className="font-medium">
                    {tx.owner.data.harbor?.timezone || '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Price</p>
                  <p className="text-lg font-semibold">
                    {formatFinnishCurrency(tx.owner.data.total_price)}
                  </p>
                </div>
                {/* Berth details */}
                <div className="pt-2">
                  <p className="text-sm text-gray-500 mb-2">Berth</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-2xs text-gray-500">Number</p>
                      <p className="font-medium">
                        {berth?.berth_number ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xs text-gray-500">Mooring Style</p>
                      <p className="font-medium">
                        {berth?.mooring_style ?? '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xs text-gray-500">Berth Price</p>
                      <p className="font-medium">
                        {typeof berth?.price === 'number'
                          ? formatFinnishCurrency(berth.price)
                          : '—'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Boat details */}
                <div className="pt-2">
                  <p className="text-sm text-gray-500 mb-2">Boat Details</p>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div>
                      <p className="text-2xs text-gray-500">Length</p>
                      <p className="font-medium">
                        {boat?.length
                          ? `${boat.length} ${boat.unit || ''}`
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xs text-gray-500">Width</p>
                      <p className="font-medium">
                        {boat?.width ? `${boat.width} ${boat.unit || ''}` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xs text-gray-500">Draft</p>
                      <p className="font-medium">
                        {boat?.draft ? `${boat.draft} ${boat.unit || ''}` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-2xs text-gray-500">Unit</p>
                      <p className="font-medium">{boat?.unit || '—'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Additional Information</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">
                    {getBookingStatusLabel(tx.owner.data.status)}
                  </p>
                </div>
                {(() => {
                  const snap =
                    (
                      tx.data as unknown as {
                        items_snapshot?: ItemsSnapshot | null;
                      }
                    ).items_snapshot || null;
                  const first =
                    snap?.items && snap.items.length > 0
                      ? snap.items[0]
                      : undefined;
                  const taxRegime = formatTaxRegime(
                    first?.tax?.tax_regime || null
                  );
                  if (!first?.tax?.tax_regime) return null;
                  return (
                    <div>
                      <p className="text-sm text-gray-500">Tax Regime</p>
                      <p className="font-medium">{taxRegime}</p>
                    </div>
                  );
                })()}
                {tx.owner.data.payment_provider && (
                  <div>
                    <p className="text-sm text-gray-500">Payment Provider</p>
                    <p className="font-medium">
                      {tx.owner.data.payment_provider.toUpperCase()}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Special Requests</p>
                  <p className="font-medium text-sm">
                    {tx.owner.data.special_requests || '—'}
                  </p>
                </div>
                {tx.owner.data.cancellation_reason && (
                  <div>
                    <p className="text-sm text-gray-500">Cancellation Reason</p>
                    <p className="font-medium text-sm">
                      {tx.owner.data.cancellation_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {tx.owner.data.booking_addons.length > 0 && (
            <div className="card md:col-span-2">
              <div className="card-body">
                <h2 className="card-title mb-4">Add-ons</h2>
                <div className="overflow-x-auto">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Add-on</th>
                        <th className="text-end">Quantity</th>
                        <th className="text-end">Price</th>
                        <th className="text-end">VAT Rate</th>
                        <th className="text-end">Gross Amount</th>
                        <th className="text-end">Slots</th>
                        <th>Period</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tx.owner.data.booking_addons.map((addon) => (
                        <tr key={addon.id}>
                          <td className="font-medium">
                            {addon.addon?.name || addon.id}
                          </td>
                          <td className="text-end">{addon.quantity}</td>
                          <td className="text-end">
                            {formatFinnishCurrency(addon.price_at_booking)}{' '}
                            {addon.price_unit_at_booking}
                          </td>
                          <td className="text-end">{addon.vat_rate ?? '-'}%</td>
                          <td className="text-end">
                            {addon.gross_amount
                              ? formatFinnishCurrency(addon.gross_amount)
                              : '--'}
                          </td>
                          <td className="text-end">{addon.no_of_slots}</td>
                          <td className="text-sm">
                            {formatDisplayDate(new Date(addon.start_date))} to{' '}
                            {formatDisplayDate(new Date(addon.end_date))}
                          </td>
                          {addon.price_unit_at_booking === 'PER_HOUR' && (
                            <td className="text-sm">
                              {`${extractTime(addon.start_date || '')} - ${extractTime(addon.end_date)}`}
                            </td>
                          )}
                          {addon.price_unit_at_booking !== 'PER_HOUR' && (
                            <td className="text-sm">Full day</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (isServiceBookingTx(tx)) {
      const owner = tx.owner;
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Service Booking Details</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Service Name</p>
                  <p className="font-medium">{owner.service.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Description</p>
                  <p className="font-medium text-sm">
                    {owner.service.description || '—'}
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Unit Price</p>
                    <p className="font-medium">
                      {formatFinnishCurrency(
                        parseFloat(owner.service.price_amount)
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Booking Slot Type</p>
                    <p className="font-medium capitalize">
                      {owner.service.booking_slot_type}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h2 className="card-title mb-4">Booking Summary</h2>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Start</p>
                    <p className="font-medium">
                      {formatDisplayDate(owner.start_date)} {owner.start_time}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">End</p>
                    <p className="font-medium">
                      {formatDisplayDate(owner.end_date)} {owner.end_time}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium">{owner.quantity}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Price</p>
                    <p className="text-lg font-semibold">
                      {formatFinnishCurrency(owner.total_price)}
                    </p>
                  </div>
                </div>
                {(() => {
                  const snap =
                    (
                      tx.data as unknown as {
                        items_snapshot?: ItemsSnapshot | null;
                      }
                    ).items_snapshot || null;
                  const first =
                    snap?.items && snap.items.length > 0
                      ? snap.items[0]
                      : undefined;
                  const taxRegime = formatTaxRegime(
                    first?.tax?.tax_regime || null
                  );
                  if (!first?.tax?.tax_regime) return null;
                  return (
                    <div>
                      <p className="text-sm text-gray-500">Tax Regime</p>
                      <p className="font-medium">{taxRegime}</p>
                    </div>
                  );
                })()}
                {owner.payment_provider && (
                  <div>
                    <p className="text-sm text-gray-500">Payment Provider</p>
                    <p className="font-medium">
                      {owner.payment_provider.toUpperCase()}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-500">Special Requests</p>
                  <p className="font-medium text-sm">
                    {owner.special_requests || '—'}
                  </p>
                </div>
                {owner.cancellation_reason && (
                  <div>
                    <p className="text-sm text-gray-500">Cancellation Reason</p>
                    <p className="font-medium text-sm">
                      {owner.cancellation_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (isLoading) {
    return (
      <Container>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="loading loading-spinner loading-lg mb-4"></div>
            <p className="text-gray-500">Loading transaction details...</p>
          </div>
        </div>
      </Container>
    );
  }

  if (!transaction) {
    return (
      <Container>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <KeenIcon
              icon="information-2"
              className="text-4xl text-gray-400 mb-4"
            />
            <p className="text-gray-500">Transaction not found</p>
            <button
              onClick={() => navigate('/transactions')}
              className="btn btn-sm btn-primary mt-4"
            >
              Back to Transactions
            </button>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mb-6">
        <button
          onClick={() => {
            if (location.state?.from) {
              navigate(location.state.from);
            } else {
              navigate('/transactions');
            }
          }}
          className="btn btn-sm btn-ghost gap-2"
        >
          <KeenIcon icon="arrow-left" className="text-lg" />
          Back
        </button>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold mb-2">
                Transaction {transaction.data.id}
              </h1>
              <p className="text-sm text-gray-500">
                Created on{' '}
                {formatDisplayDateTime(new Date(transaction.data.created_at))}
              </p>
            </div>
            <span
              className={`badge badge-lg ${getStatusBadgeClass(transaction.data.status)}`}
            >
              {transaction.data.status}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-base-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Transaction Type</p>
              <p className="text-lg font-semibold">{transaction.data.type}</p>
            </div>
            <div className="bg-base-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Amount</p>
              <p className="text-lg font-semibold">
                {formatFinnishCurrency(transaction.data.amount)}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Refunded</p>
              <p className="text-lg font-semibold">
                {formatFinnishCurrency(transaction.data.refunded_amount)}
              </p>
            </div>
            <div className="bg-base-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Currency</p>
              <p className="text-lg font-semibold">
                {transaction.data.currency}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <div className="bg-base-200 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">PSP Fee</p>
              <p className="text-lg font-semibold">
                {(() => {
                  const fee = parsePspFee(
                    (transaction.data as unknown as { psp_fee?: unknown })
                      .psp_fee
                  );
                  return typeof fee === 'number'
                    ? formatFinnishCurrency(fee)
                    : '—';
                })()}
              </p>
            </div>
            {(() => {
              const commissionOutcome = (
                transaction as unknown as { commission_outcome?: unknown }
              ).commission_outcome;
              const parsedCommission =
                typeof commissionOutcome === 'number'
                  ? commissionOutcome
                  : typeof commissionOutcome === 'string'
                    ? Number(commissionOutcome)
                    : null;
              const isValidCommission =
                parsedCommission !== null && Number.isFinite(parsedCommission);
              if (!isValidCommission) return null;
              return (
                <div className="bg-base-200 rounded-lg">
                  <p className="text-sm text-gray-500 mb-1">
                    Commission Amount
                  </p>
                  <p className="text-lg font-semibold">
                    {formatFinnishCurrency(parsedCommission)}
                  </p>
                </div>
              );
            })()}
          </div>

          {transaction.data.netvisor_posting_eligible && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-100 p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    Netvisor Invoice Posting
                  </p>
                  <span
                    className={`badge ${getNetvisorPostingStatusBadgeClass(transaction.data.netvisor_posting_status || '')}`}
                  >
                    {NETVISOR_POSTING_STATUS_LABELS[
                      transaction.data.netvisor_posting_status || ''
                    ] || transaction.data.netvisor_posting_status}
                  </span>
                  {transaction.data.netvisor_posting_status === 'POSTED' && (
                    <p className="mt-2 text-sm text-gray-700">
                      Invoice ID:{' '}
                      <span className="font-mono">
                        {transaction.data.netvisor_invoice_id || '—'}
                      </span>
                    </p>
                  )}
                  {transaction.data.netvisor_posting_status === 'FAILED' && (
                    <p className="mt-2 text-sm text-gray-700 break-words">
                      {transaction.data.netvisor_export_error
                        ? transaction.data.netvisor_export_error.replace(
                            /_/g,
                            ' '
                          )
                        : 'Netvisor invoice posting failed.'}
                    </p>
                  )}
                  {transaction.data.netvisor_posting_status ===
                    'NOT_REQUIRED' && (
                    <p className="mt-2 text-sm text-gray-700">
                      No Netvisor posting is required for this transaction
                      (zero commission).
                    </p>
                  )}
                </div>
                {(transaction.data.netvisor_posting_status === 'FAILED' ||
                  transaction.data.netvisor_posting_status === 'PENDING') && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setIsNetvisorRetryConfirmOpen(true)}
                    disabled={
                      !hasNetvisorRetryPermission || isRetryingNetvisorInvoice
                    }
                    aria-disabled={
                      !hasNetvisorRetryPermission || isRetryingNetvisorInvoice
                    }
                  >
                    {isRetryingNetvisorInvoice ? 'Retrying…' : 'Retry'}
                  </button>
                )}
              </div>
              {(transaction.data.netvisor_posting_status === 'FAILED' ||
                transaction.data.netvisor_posting_status === 'PENDING') &&
                !hasNetvisorRetryPermission && (
                  <p className="mt-2 text-sm text-gray-600">
                    Retry is disabled due to missing permissions.
                  </p>
                )}
            </div>
          )}

          {(() => {
            const dataWithSnapshot = transaction.data as unknown as {
              items_snapshot?: ItemsSnapshot | null;
              net_amount?: number | string | null;
              vat_amount?: number | string | null;
              gross_amount?: number | string | null;
              vat_rate?: number | string | null;
              product_type?: string | null;
              tax_regime?: string | null;
              customer_country?: string | null;
            };
            const snap = dataWithSnapshot.items_snapshot;
            const firstItem =
              snap?.items && snap.items.length > 0 ? snap.items[0] : undefined;
            const totals = snap?.totals || {};
            const topLevelNet =
              typeof dataWithSnapshot.net_amount === 'number'
                ? dataWithSnapshot.net_amount
                : typeof dataWithSnapshot.net_amount === 'string'
                  ? Number(dataWithSnapshot.net_amount)
                  : undefined;
            const topLevelVat =
              typeof dataWithSnapshot.vat_amount === 'number'
                ? dataWithSnapshot.vat_amount
                : typeof dataWithSnapshot.vat_amount === 'string'
                  ? Number(dataWithSnapshot.vat_amount)
                  : undefined;
            const topLevelGross =
              typeof dataWithSnapshot.gross_amount === 'number'
                ? dataWithSnapshot.gross_amount
                : typeof dataWithSnapshot.gross_amount === 'string'
                  ? Number(dataWithSnapshot.gross_amount)
                  : undefined;
            const hasSummary =
              !!snap ||
              Number.isFinite(topLevelNet) ||
              Number.isFinite(topLevelVat) ||
              Number.isFinite(topLevelGross);
            if (!hasSummary) return null;
            const vatRateValue =
              typeof firstItem?.vat_rate === 'number'
                ? firstItem.vat_rate
                : dataWithSnapshot.vat_rate !== undefined &&
                    dataWithSnapshot.vat_rate !== null &&
                    dataWithSnapshot.vat_rate !== ''
                  ? dataWithSnapshot.vat_rate
                  : null;
            const productLabel = formatProductType(
              firstItem?.product_type || dataWithSnapshot.product_type || null
            );
            const customerCountry = formatCountryName(
              firstItem?.tax?.customer_country ||
                dataWithSnapshot.customer_country ||
                null
            );
            const taxRegimeLabel = formatTaxRegime(
              firstItem?.tax?.tax_regime || dataWithSnapshot.tax_regime || null
            );

            return (
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-3">
                  Booking Amount Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Net Amount</p>
                    <p className="text-xl font-semibold">
                      {typeof totals.net_amount === 'number'
                        ? formatFinnishCurrency(totals.net_amount)
                        : Number.isFinite(topLevelNet)
                          ? formatFinnishCurrency(topLevelNet as number)
                          : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">VAT Amount</p>
                    <p className="text-xl font-semibold">
                      {typeof totals.vat_amount === 'number'
                        ? formatFinnishCurrency(totals.vat_amount)
                        : Number.isFinite(topLevelVat)
                          ? formatFinnishCurrency(topLevelVat as number)
                          : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Gross Amount</p>
                    <p className="text-xl font-semibold">
                      {typeof totals.gross_amount === 'number'
                        ? formatFinnishCurrency(totals.gross_amount)
                        : Number.isFinite(topLevelGross)
                          ? formatFinnishCurrency(topLevelGross as number)
                          : '—'}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">VAT Rate</p>
                    <p className="text-lg font-semibold">
                      {vatRateValue !== null
                        ? formatFinnishPercentage(vatRateValue)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Product</p>
                    <p className="text-lg font-semibold">{productLabel}</p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Tax Regime</p>
                    <p className="text-lg font-semibold">{taxRegimeLabel}</p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Customer Country
                    </p>
                    <p className="text-lg font-semibold">{customerCountry}</p>
                  </div>
                </div>
              </div>
            );
          })()}
          {(() => {
            const discount = (
              transaction.data.items_snapshot as
                | ItemsSnapshot
                | null
                | undefined
            )?.discount;
            if (!discount) return null;
            const originalNet =
              discount.original_net_amount ?? discount.original_amount;
            const finalNet = discount.final_net_amount ?? discount.final_amount;
            return (
              <div className="mt-6">
                <h2 className="text-lg font-semibold mb-3">Discount Applied</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {discount.code && (
                    <div className="bg-base-200 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">
                        Discount Code
                      </p>
                      <p className="text-lg font-semibold font-mono">
                        {discount.code}
                      </p>
                    </div>
                  )}
                  {discount.label && (
                    <div className="bg-base-200 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Label</p>
                      <p className="text-lg font-semibold">{discount.label}</p>
                    </div>
                  )}
                  {discount.discount_type && (
                    <div className="bg-base-200 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Type</p>
                      <p className="text-lg font-semibold capitalize">
                        {discount.discount_type}
                      </p>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">Discount Value</p>
                    <p className="text-lg font-semibold">
                      {typeof discount.discount_value === 'number'
                        ? discount.discount_type === 'percentage'
                          ? formatFinnishPercentage(discount.discount_value)
                          : formatFinnishCurrency(discount.discount_value)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Discount Amount
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof discount.discount_amount === 'number'
                        ? formatFinnishCurrency(discount.discount_amount)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Original Net Amount
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof originalNet === 'number'
                        ? formatFinnishCurrency(originalNet)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Final Net Amount
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof finalNet === 'number'
                        ? formatFinnishCurrency(finalNet)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Original Gross Amount
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof discount.original_gross_amount === 'number'
                        ? formatFinnishCurrency(discount.original_gross_amount)
                        : '—'}
                    </p>
                  </div>
                  <div className="bg-base-200 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">
                      Final Gross Amount
                    </p>
                    <p className="text-lg font-semibold">
                      {typeof discount.final_gross_amount === 'number'
                        ? formatFinnishCurrency(discount.final_gross_amount)
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {transaction.data.user && (
        <div className="card mb-6">
          <div className="card-body">
            <h2 className="card-title mb-4">User Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">User ID</p>
                <p className="font-medium">{transaction.data.user.id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">
                  {transaction.data.user.name || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium break-all">
                  {transaction.data.user.email}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Billing Address</p>
                <p className="font-medium break-all">
                  {transaction.data.user.billing_address_line_1 || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">City</p>
                <p className="font-medium">
                  {transaction.data.user.city || '—'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Post Code</p>
                <p className="font-medium">
                  {transaction.data.user.post_code || '—'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card mb-6">
        <div className="card-body">
          <h2 className="card-title mb-4">Payment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {transaction.data.payment_intent_id && (
              <div>
                <p className="text-sm text-gray-500">Stripe Payment Intent</p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.payment_intent_id}
                </p>
              </div>
            )}
            {transaction.data.paytrail_payment_id && (
              <div>
                <p className="text-sm text-gray-500">Paytrail Transaction ID</p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.paytrail_payment_id}
                </p>
              </div>
            )}
            {transaction.data.stripe_refund_id && (
              <div>
                <p className="text-sm text-gray-500">
                  {transaction.data.payment_provider?.toLowerCase() ===
                    'paytrail' || transaction.data.paytrail_payment_id
                    ? 'Paytrail Refund ID'
                    : 'Stripe Refund ID'}
                </p>
                <p className="font-mono text-sm break-all">
                  {transaction.data.stripe_refund_id}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {renderTransactionDetails(transaction)}

      <ConfirmationDialog
        open={isNetvisorRetryConfirmOpen}
        onClose={() => setIsNetvisorRetryConfirmOpen(false)}
        onConfirm={handleRetryNetvisorInvoice}
        confirmButtonText="Retry"
        message={
          <p className="text-sm text-gray-700">
            This will retry Netvisor invoice posting for this transaction.
          </p>
        }
        disabled={isRetryingNetvisorInvoice}
        isLoading={isRetryingNetvisorInvoice}
      />
    </Container>
  );
};

export default TransactionDetails;
